import { Component, ElementRef, Input, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/material.module';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControlType } from 'src/app/shared/enums';
import { FormButtonComponent } from './form-button/form-button.component';
import { ToastService } from 'src/app/services/toast.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { IFormConfig, IFormItem, ILayout } from 'src/app/interfaces/IForm';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { FormDatePickerComponent } from '../form-date-picker/form-date-picker.component';
import { SelectFileComponent } from '../select-file/select-file.component';
import { FormSelectComponent } from '../select/form-select.component';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-dynamic-form',
  imports: [
    SharedModule,
    MaterialModule,
    CommonModule,
    ReactiveFormsModule,
    FormSelectComponent,
    AutocompleteComponent,
    SelectFileComponent,
    FormDatePickerComponent,
    TranslateModule,
    FormButtonComponent
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrl: './dynamic-form.component.scss',
  host: { '[class.login-form]': "appearance === 'login'" }
})
export class DynamicFormComponent implements OnDestroy {
  @ViewChildren('dynamicElements') dynamicElements: QueryList<ElementRef>;
  @Input() formConfigs: IFormConfig;
  @Input() formGroup: FormGroup = new FormGroup({});
  @Input() spinLoader: boolean;
  @Input() onSubmit?: () => void;
  @Input() appearance: 'default' | 'login' = 'default';
  unsubscribe: Subscription[] = [];
  controlType = FormControlType;

  constructor(
    private toastService: ToastService,
    private translateService: TranslateService) { }

  // set error from backend
  set ValidatorError(errors: any) {
    Object.keys(errors).forEach((field) => {
      const control = this.formGroup.get(field);
      if (control) {
        const translatedErrors = errors[field].map((error: any) => this.translateService.instant(error));
        control.setErrors({ message: translatedErrors });
      }
    });
    if (errors.Message) {
      this.toastService.error(this.translateService.instant(errors.Message));
    }
  }

  getErrorMessage(item: IFormItem): string[] {
    const { key, validator } = item;
    const control = this.formGroup?.get(key);
    let errorMessage: string[] = [];

    if (control && (control.touched || control.dirty) && control.invalid) {
      // Add error messages from validators
      validator?.forEach(error => {
        if (control.hasError(error.errorType)) {
          errorMessage.push(this.translateService.instant(error.errorMessage));
        }
      });

      // Add error messages from backend
      const backendErrors = control.errors?.['message'];
      if (backendErrors) {
        errorMessage = errorMessage.concat(Array.isArray(backendErrors) ? backendErrors : [backendErrors]);
      }
    }

    return errorMessage;
  }

  onBlur(item: any, event: FocusEvent) {
    const value = (event.target as HTMLInputElement).value;
    if (value && typeof item.blur === 'function') {
      item.blur(value);
    }
  }

  hasRequired(item: IFormItem) {
    return item.validator?.some(error => error.errorType === 'required');
  }

  toggleShowPassword(event: Event, input: IFormItem) {
    event.preventDefault();
    input.inputConfig && (input.inputConfig.showingPassword = !input.inputConfig.showingPassword);
  }

  getItemClass({ layoutConfig, itemClass }: any) {
    const layoutClasses = layoutConfig?.map((col: ILayout) => (col.screenSize ? `col-${col.screenSize}-${col.column}` : `col-${col.column}`)).join(' ') || '';
    const additionalClass = itemClass ? ` ${itemClass}` : '';
    return layoutClasses === '' ? additionalClass : `${layoutClasses}${additionalClass}`;
  }

  handleEnter(event: Event) {
    event.preventDefault();
    if (!this.formConfigs.preventEnterSubmit) {
      this.onSubmit?.();
    }
  }

  onChange(event: MatCheckboxChange, item: any, option: any) {
    const defaultValue = this.formGroup.get(item.key)?.value;
    if (defaultValue !== option.value) {
      this.formGroup.patchValue({ [item.key]: option.value });
    } else if (defaultValue === option.value) {
      this.formGroup.patchValue({ [item.key]: null });
    }
    item.change?.({ event, option });
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
